/** Digital files of a product (sent to buyers as download links after payment). Rendered in the product editor. */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Empty, List, Popconfirm, Select, Space, Typography, Upload, message } from 'antd';
import { DeleteOutlined, FileOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET, directUpload } from './api';
import { Hint, listOf, toastErr, useSite } from './common';
import { fmtSize, reportStorageError } from './Media';

const { Text } = Typography;

export const ProductDigitalFiles = ({ productId, variants = [] }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [files, setFiles] = useState(null);
    const [variantId, setVariantId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null);
    const base = `/products/${productId}/digital-files`;

    const load = useCallback(() => {
        api.get(base, null, QUIET).then((d) => setFiles(listOf(d))).catch((e) => { setFiles(e?.status === 404 ? null : []); });
    }, [api, base]);
    useEffect(() => { if (productId) load(); }, [productId, load]);

    const upload = async (file) => {
        setBusy(true);
        setProgress(0);
        try {
            const { key } = await directUpload(api, file, 'digital', { onProgress: setProgress });
            await api.post(`${base}/complete`, { key, variant_id: variantId || undefined });
            message.success(t('Uploaded'));
            load();
        } catch (e) {
            if (!reportStorageError(e)) toastErr(e, t('Upload failed'));
        } finally { setBusy(false); setProgress(null); }
        return false;
    };
    const del = async (f) => { try { await api.del(`${base}/${f.id}`); load(); } catch (e) { toastErr(e); } };

    if (!productId || files === null) return null;
    const vOpts = variants.filter((v) => v.id).map((v) => ({ value: v.id, label: v.title || `#${v.id}` }));
    const vName = (id) => vOpts.find((o) => o.value === id)?.label;
    return (
        <Card title={<span>{t('Digital downloads')}<Hint title={t('Buyers get download links by email after payment')} /></span>} style={{ marginBottom: 16 }}
            extra={(
                <Space>
                    {vOpts.length > 1 && <Select size="small" allowClear placeholder={t('All variants')} value={variantId} onChange={setVariantId} options={vOpts} style={{ width: 150 }} />}
                    <Upload showUploadList={false} beforeUpload={upload}><Button size="small" icon={<UploadOutlined />} loading={busy}>{progress !== null ? `${progress}%` : t('Add file')}</Button></Upload>
                </Space>
            )}>
            {!files.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No files — this is a physical product')} /> : (
                <List size="small" dataSource={files} renderItem={(f) => (
                    <List.Item actions={[<Popconfirm key="d" title={t('Delete?')} onConfirm={() => del(f)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>]}>
                        <Space><FileOutlined /><Text>{f.name}</Text><Text type="secondary" style={{ fontSize: 12 }}>{fmtSize(f.bytes)}{f.variant_id ? ` · ${vName(f.variant_id) || `#${f.variant_id}`}` : ''}</Text></Space>
                    </List.Item>
                )} />
            )}
        </Card>
    );
};

export default ProductDigitalFiles;
